import { matchingMvfScalesSchema } from "@lib/matchingMvf";
import { mvfSchema } from "@lib/tranche";
import { trackSchema } from "@lib/types";
import { z } from "zod";
import {
  roundApplicationAudienceSchema,
  roundApplicationWindowSchema,
  entityType,
  evaluationFinalitySchema,
  evaluatorTypeSchema,
  fieldSchema,
  fieldSourceSchema,
  fieldStatusSchema,
  keyIndividualSchema,
  proposalWithFieldResponses,
  trackStatusSchema,
} from "./aggregate";

const userId = z.string().startsWith("u-");
const roundId = z.string().startsWith("r-");
const proposalId = z.string().startsWith("p-");
const proposalFieldId = z.string().startsWith("field-");
const proposalFormId = z.string().startsWith("form-");
const agentTokenId = z.string().startsWith("at-");
const funderId = z.string().startsWith("f-");
const recommenderId = z.string().startsWith("rec-");
const evaluableId = z.union([proposalId, recommenderId]);
const evaluatorId = z.union([
  funderId,
  recommenderId,
  z.string().startsWith("e-"),
]);

// The action's creation time (ISO string), stamped when the action is created
// (the Client dispatch chokepoint / the round-creation route). This is action
// DATA, carried in the payload for every type whose application persists a
// time (member createdAt, evaluation lastTouchedAt, disclosure/recusal-vote
// timestamps, wizard proposal submit datetimes) — so applying the action is a
// pure function of (round, payload). The message-ENVELOPE timestamp differs
// between the optimistic apply (client-stamped) and the server's confirm
// re-apply (server-stamped), which made those persisted fields
// non-deterministic; the reducer no longer reads it.
//
// Persisted actions predating this field were backfilled from the envelope
// timestamp (2026-07); rows the backfill missed and upcast-synthesized
// payloads are covered by fallback stamping in the reducer's performAction
// and at the DB-row load, and pre-timestamp queued edits in a client's
// IndexedDB are stamped by the queue migration in SPAStorageAdapter.init.
const actionTimestamp = z.string();

const roundDescriptionSchema = z.string().trim().max(5000);

export const createRound = z
  .object({
    type: z.literal("CREATE_ROUND"),
    timestamp: actionTimestamp,
    // Version 1 is legacy and did not require roundIdentifier for SFC rounds
    // (the upcast defaults it to "2025").
    // Version 2 required roundIdentifier for SFC rounds.
    // Version 3 has no round-level identifier: it belongs to the proposal form
    // (ADD_PROPOSAL_FORM), since it keys into that form's spreadsheet. A v3
    // action may still carry one — the wizard translation passes through what
    // legacy wizard events recorded — and the reducer stores it as the legacy
    // fallback.
    version: z.union([z.literal(1), z.literal(2), z.literal(3)]).optional(),
    name: z.string().trim().min(1, "Name is required"),
    description: roundDescriptionSchema.optional(),
    isSfcRound: z.boolean().optional(),
    // Defaults to true (a normal track round) when omitted. When false, the
    // round is "trackless": ordinary recommenders sit in MAIN and track UI is hidden.
    usesTracks: z.boolean().optional(),
    // Omitted in historical events; new submissions default to false.
    allowsAnonymization: z.boolean().optional(),
    roundIdentifier: z.string().trim().optional(),
    defaultProposalFormId: proposalFormId,
    holdProposalId: proposalId,
    holdProposalName: z.string(),
    initialFunderId: funderId.optional(),
    initialRecommenderId: recommenderId.optional(),

    // Information about the round member who is creating the round
    userId,
    initials: z.string().trim().min(1, "Initials are required"),
    track: trackSchema,
    deanonymize: z.boolean().optional(),
  })
  .superRefine((value, ctx) => {
    if (
      value.version === 2 &&
      value.isSfcRound !== false &&
      (value.roundIdentifier == null || value.roundIdentifier.trim() === "")
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["roundIdentifier"],
        message: "Round identifier is required for SFC rounds",
      });
    }
  });

// ADD_ROUND_MEMBER is used by the *SFC-only round modal* <AddUserModal /> on the admin/round-members page (<RoundMembers />).
//
// Note also the related CREATE_OR_UPDATE_ROUND_MEMBER
const addRoundMember = z.object({
  type: z.literal("ADD_ROUND_MEMBER"),
  timestamp: actionTimestamp,
  userId,
  initials: z.string(),
  track: trackSchema,
  deanonymize: z.boolean().optional(),
});

const textQuestionSchema = z.object({
  status: fieldStatusSchema,
  type: z.literal("question"),
  id: proposalFieldId,
  title: z.string(),
  description: z.string().optional(),
  externalId: z.string().optional(),
  source: fieldSourceSchema,
});

const sectionHeaderSchema = z.object({
  status: fieldStatusSchema,
  type: z.literal("sectionHeader"),
  id: proposalFieldId,
  title: z.string(),
  description: z.string().optional(),
  externalId: z.string().optional(),
  source: fieldSourceSchema,
});

const fieldCreateSchema = z.discriminatedUnion("type", [
  textQuestionSchema,
  sectionHeaderSchema,
]);

const addOrUpdateProposalFormField = z.object({
  type: z.literal("ADD_OR_UPDATE_PROPOSAL_FORM_FIELD"),
  proposalFormId,
  field: fieldCreateSchema,
});

export const payloadSchema = z.object({
  proposalFormId,
  field: fieldCreateSchema,
});

const addProposalForm = z.object({
  type: z.literal("ADD_PROPOSAL_FORM"),
  proposalFormId,
  googleFormId: z.string(),
  spreadsheetId: z.string(),
  spreadsheetRange: z.string(),
  name: z.string(),
  // Matched against the spreadsheet's Round column on sync. Optional only so
  // actions recorded before it existed still parse; those forms fall back to
  // the round's legacy identifier. The Import & Export page always sends one.
  roundIdentifier: z.string().trim().min(1).optional(),
});

const addEvaluator = z.object({
  type: z.literal("ADD_EVALUATOR"),
  memberId: userId,
  evaluatorId,
  shortname: z.string(),
  evaluatorType: evaluatorTypeSchema,
  threshold: z.number().int().optional(),
});

export const addProposal = z.object({
  type: z.literal("ADD_PROPOSAL"),
  proposalFormId,
  proposalId,
  entityType,
  proposalFirstSubmittedDatetime: z.string(),
  proposalShortname: z.string(),
  proposalLongname: z.string().optional(),
  proposalExternalId: z.string().optional(),
  // Whether the applicant asked for a matching pledge. When omitted — every
  // ADD_PROPOSAL before this field existed, and callers with no applicant
  // answer to pass — the reducer derives it from `matchRate` (positive means
  // yes), which was the rule before the field existed. Pass it explicitly to
  // record a "no" alongside a match rate.
  requestsMatchingPledge: z.boolean().optional(),
  matchRate: z.number().optional(),
  maxStandardAmount: z.number().optional(),
  baseRequestAmount: z.number().optional(),
  targetsFreedom: z.boolean().optional(),
  targetsFairness: z.boolean().optional(),
  longformLink: z.string().optional(),
  keyIndividuals: z.array(keyIndividualSchema).optional(),
  fieldResponses: z.record(z.string(), z.string()).optional(),
});

const startEvaluation = z.object({
  type: z.literal("START_EVALUATION"),
  timestamp: actionTimestamp,
  evaluatorId,
  evaluableId,
  mvf: mvfSchema.optional(),
});

const removeRound = z.object({ type: z.literal("REMOVE_ROUND") });
const removeRoundMember = z.object({
  type: z.literal("REMOVE_ROUND_MEMBER"),
  userId,
});
const removeProposalForm = z.object({
  type: z.literal("REMOVE_PROPOSAL_FORM"),
  proposalFormId,
});
// Corrects the identifier a spreadsheet-backed form syncs against, e.g. a
// typo at attach time, or a form attached by a client predating the field.
// Attach a published Application Builder form as a proposal source. The
// mapping from its questions onto the round's fields starts empty and is
// filled in by the change actions below; the round, not the form, owns it.
//
// These four are SFC-admin only and server-only (see SERVER_ONLY_ACTION_TYPES
// and the deny-by-default cases in permissions.ts): they reach the engine
// through the Proposal Forms routes, which check the form exists and is
// published — something the reducer can't see.
const attachApplicationForm = z.object({
  type: z.literal("ATTACH_APPLICATION_FORM"),
  // New attachments enforce one active form; historical multi-form events remain replayable.
  singleForm: z.literal(true).optional(),
  proposalFormId,
  formId: z.string().min(1),
  name: z.string(),
});

// One submitted response has one stable proposal identity in an attachment.
const applicationResponseInclusion = z.object({
  type: z.literal("APPLICATION_RESPONSE_INCLUSION"),
  refresh: z.boolean().optional(),
  responseRevision: z.string().optional(),
  syncIssue: z.string().optional(),
  proposalFormId,
  formId: z.string().min(1),
  proposalId,
  included: z.boolean(),
  proposal: proposalWithFieldResponses.optional(),
  fields: z.array(fieldSchema),
});

// Stop reading applications from a builder form. The proposal form and every
// proposal under it stay in the round; only the attachment goes.
const detachApplicationForm = z.object({
  type: z.literal("DETACH_APPLICATION_FORM"),
  proposalFormId,
});

// Point a round field at a question (or clear it with null). `fieldId` is a
// plain string rather than the round-field enum so an action recorded for a
// field later retired from the registry still replays.
const applicationFormFieldMappingChange = z.object({
  type: z.literal("APPLICATION_FORM_FIELD_MAPPING_CHANGE"),
  proposalFormId,
  fieldId: z.string().min(1),
  questionId: z.string().min(1).nullable(),
});

// Say what one option of a field's question means to the round (or clear it).
// `optionId` is an option id from the form, or OTHER_OPTION_ID for the
// question's free-text "Other" answer.
const applicationFormOptionValueChange = z.object({
  type: z.literal("APPLICATION_FORM_OPTION_VALUE_CHANGE"),
  proposalFormId,
  fieldId: z.string().min(1),
  optionId: z.string().min(1),
  value: z.string().min(1).nullable(),
});

const proposalFormRoundIdentifierChange = z.object({
  type: z.literal("PROPOSAL_FORM_ROUND_IDENTIFIER_CHANGE"),
  proposalFormId,
  roundIdentifier: z.string().trim().min(1, "Round identifier is required"),
});
const removeProposalFormField = z.object({
  type: z.literal("REMOVE_PROPOSAL_FORM_FIELD"),
  proposalFormId,
  fieldId: proposalFieldId,
});
const removeEvaluator = z.object({
  type: z.literal("REMOVE_EVALUATOR"),
  evaluatorId,
});
const removeProposal = z.object({
  type: z.literal("REMOVE_PROPOSAL"),
  // Server-only deletion metadata. Older servers still replay the removal.
  deletedResponseId: z.string().optional(),
  proposalFormId,
  proposalId,
});
const readdProposal = z.object({
  type: z.literal("READD_PROPOSAL"),
  proposalFormId,
  proposalId,
});

export const roundDescriptionChange = z.object({
  type: z.literal("ROUND_DESCRIPTION_CHANGE"),
  roundId,
  description: roundDescriptionSchema,
});

export const roundNameChange = z.object({
  type: z.literal("ROUND_NAME_CHANGE"),
  roundId,
  newName: z.string().trim().min(1, "Name is required"),
});

// Archive (read-only, out of the everyday lists) or restore a round. One
// action for both directions so there is a single permission and a single
// code path; `timestamp` becomes the round's archivedAt.
export const roundArchivedChange = z.object({
  type: z.literal("ROUND_ARCHIVED_CHANGE"),
  timestamp: actionTimestamp,
  roundId,
  archived: z.boolean(),
  // Trusted cleanup of an already-purged response. The idempotent archive
  // envelope remains replayable by rollback builds with an archived freeze.
  applicationDeletion: z
    .object({
      proposalFormId,
      proposalId,
      deletedResponseId: z.string(),
    })
    .optional(),
});

export const roundCompletedChange = z.object({
  type: z.literal("ROUND_COMPLETED_CHANGE"),
  timestamp: actionTimestamp,
  roundId,
  completed: z.boolean(),
});

export const roundApplicationsOpenChange = z.object({
  type: z.literal("ROUND_APPLICATIONS_OPEN_CHANGE"),
  roundId,
  open: z.boolean(),
});

export const roundApplicationAccessChange = z.object({
  type: z.literal("ROUND_APPLICATION_ACCESS_CHANGE"),
  roundId,
  open: z.boolean(),
  audience: roundApplicationAudienceSchema,
  applicationWindow: roundApplicationWindowSchema.optional(),
});

export const adminChangeRoundMemberProperty = z.object({
  type: z.literal("ADMIN_CHANGE_ROUND_MEMBER_PROPERTY"),
  memberId: userId,
  track: trackSchema.optional(),
  initials: z.string().optional(),
  admin: z.boolean().optional(),
  isRestrictedFunder: z.boolean().optional(),
  hiddenRecommenderMemberIds: z.array(z.string()).optional(),
});

export const roundMemberPropertyChange = z.object({
  type: z.literal("ROUND_MEMBER_PROPERTY_CHANGE"),
  memberId: userId,
  feedbackVisibleByDefault: z.boolean().optional(),
  deanonymize: z.boolean().optional(),
});

const authorizeAgentTokenForRound = z.object({
  type: z.literal("AUTHORIZE_AGENT_TOKEN_FOR_ROUND"),
  tokenId: agentTokenId,
});

const deauthorizeAgentTokenForRound = z.object({
  type: z.literal("DEAUTHORIZE_AGENT_TOKEN_FOR_ROUND"),
  tokenId: agentTokenId,
});

export const evaluatorPropertyChange = z.object({
  type: z.literal("EVALUATOR_PROPERTY_CHANGE"),
  evaluatorId,
  property: z.literal("shortname"),
  value: z.string(),
});

export const evaluationFeedbackChange = z.object({
  type: z.literal("EVALUATION_FEEDBACK_CHANGE"),
  timestamp: actionTimestamp,
  memberId: userId,
  evaluableId,
  positive: z.string().optional(),
  negative: z.string().optional(),
});

export const evaluationNotesChange = z.object({
  type: z.literal("EVALUATION_NOTES_CHANGE"),
  timestamp: actionTimestamp,
  memberId: userId,
  evaluableId,
  notes: z.string(),
});

// Round-level note a recommender writes for the funder, not proposal-scoped
// (SP-2152). Mirrors evaluationNotesChange without evaluableId.
export const generalNotesChange = z.object({
  type: z.literal("GENERAL_NOTES_CHANGE"),
  memberId: userId,
  notes: z.string(),
});

const notesAndFeedbackVisibility = z.enum(["default", "visible", "hidden"]);

export const notesAndFeedbackVisibilityChange = z.object({
  type: z.literal("NOTES_AND_FEEDBACK_VISIBILITY_CHANGE"),
  memberId: userId,
  evaluableId,
  visibility: notesAndFeedbackVisibility,
});

const evaluationStatusChangeOptions = z.union([
  z.literal("start"),
  z.literal("cancel"),
  z.literal("hide"),
  z.literal("unhide"),
  z.literal("recuse"),
  z.literal("defer"),
]);

// Deprecated
export const evaluationStatusChange = z.object({
  type: z.literal("EVALUATION_STATUS_CHANGE"),
  evaluatorId,
  evaluableId,
  change: evaluationStatusChangeOptions,
  // Must be set iff change === "defer"
  deferredTo: z.string().optional(),
  // Imported event from when the transition rules may have been different.
  // Can make transitions that are not allowed in the current app.
  // Never use this in the app.
  legacy: z.literal(true).optional(),
});

const evaluationStateChangeOptions = z.union([
  z.literal("start"),
  z.literal("cancel"),
  z.literal("recuse"),
  z.literal("defer"),
]);

export const evaluationStateChange = z.object({
  type: z.literal("EVALUATION_STATE_CHANGE"),
  timestamp: actionTimestamp,
  evaluatorId,
  evaluableId,
  change: evaluationStateChangeOptions,
  // Must be set iff change === "defer"
  deferredTo: z.string().optional(),
  // Imported event from when the transition rules may have been different.
  // Can make transitions that are not allowed in the current app.
  // Never use this in the app.
  legacy: z.literal(true).optional(),
});

export const evaluationSetHidden = z.object({
  type: z.literal("EVALUATION_SET_HIDDEN"),
  timestamp: actionTimestamp,
  evaluatorId,
  evaluableId,
  hidden: z.boolean(),
});

export const evaluationMvfChange = z.object({
  type: z.literal("EVALUATION_MVF_CHANGE"),
  timestamp: actionTimestamp,
  evaluatorId,
  evaluableId,
  mvf: mvfSchema,
  setZero: z.boolean().optional(),
});

export const matchingMvfChange = z.object({
  type: z.literal("MATCHING_MVF_CHANGE"),
  timestamp: actionTimestamp,
  evaluatorId,
  evaluableId,
  matchingMvfScales: matchingMvfScalesSchema,
});

const evaluationSetZero = z.object({
  type: z.literal("EVALUATION_SET_ZERO"),
  timestamp: actionTimestamp,
  evaluatorId,
  evaluableId,
});

// Record (`finality`) or retract (`finality: null`) the recommender's final
// verdict on an evaluable. Pure annotation: this never writes the MVF —
// EVALUATION_SET_ZERO remains the way to zero a curve.
export const evaluationFinalityChange = z.object({
  type: z.literal("EVALUATION_FINALITY_CHANGE"),
  timestamp: actionTimestamp,
  evaluatorId,
  evaluableId,
  finality: evaluationFinalitySchema.nullable(),
});
// Note: undo/redo restores of this action bypass the reducer's availability
// gates via the message envelope's `undoes` (see the reducer case and
// ActionMeta) — NOT via a payload field a client could set on a fresh
// dispatch.

// The recommender's standing claim that an evaluable is actively harmful.
//
// Its own action type, and deliberately independent of everything else the
// recommender records. An MVF curve must end at value 0 and can only decrease
// toward it, so no point on one can ever be negative: the curve cannot
// express harm at all, which is why this is a separate statement rather than
// a shape the chart could have taken. It follows that a drawn curve and this
// flag never contradict each other — they answer different questions ("how
// much would I fund this" vs "do I think it does damage") — so nothing here
// reads or writes the MVF, and no curve edit retracts it.
export const evaluationNegativeValueChange = z.object({
  type: z.literal("EVALUATION_NEGATIVE_VALUE_CHANGE"),
  timestamp: actionTimestamp,
  evaluatorId,
  evaluableId,
  negativeValue: z.boolean(),
});

// This event is used for both normalizing all evaluations *and undoing
// normalization*. If `restoreMvfProfile` is not present the event is
// normalizing, and if it is present the event is undoing a normalization.
export const evaluationsNormalize = z.object({
  type: z.literal("NORMALIZE_EVALUATIONS"),
  evaluatorId,
  restoreMvfProfile: z.record(z.string(), mvfSchema).optional(),
});

export const evaluationExpectedMatchingFractionChange = z.object({
  type: z.literal("SET_EXPECTED_MATCHING_FRACTION"),
  evaluatorId,
  evaluableId,
  expectedMatchingFraction: z.number(),
});

export const incrementalEvalsPush = z.object({
  type: z.literal("INCREMENTAL_EVALS_PUSH"),
  evaluatorId,
  evaluableId,
  incrementAmount: z.number(),
});

const incrementalEvalsUndo = z.object({
  type: z.literal("INCREMENTAL_EVALS_UNDO"),
  evaluatorId,
});

const incrementalEvalsRedo = z.object({
  type: z.literal("INCREMENTAL_EVALS_REDO"),
  evaluatorId,
});

export const proposalFormFieldChange = z.object({
  type: z.literal("PROPOSAL_FORM_FIELD_CHANGE"),
  proposalFormId,
  field: z.discriminatedUnion("type", [
    textQuestionSchema,
    sectionHeaderSchema,
  ]),
});

export const proposalLongnameChange = z.object({
  type: z.literal("PROPOSAL_LONGNAME_CHANGE"),
  proposalId,
  proposalFormId,
  longname: z.string(),
});

export const proposalShortnameChange = z.object({
  type: z.literal("PROPOSAL_SHORTNAME_CHANGE"),
  proposalId,
  proposalFormId,
  shortname: z.string(),
});

export const proposalFieldResponseChange = z.object({
  type: z.literal("PROPOSAL_FIELD_RESPONSE_CHANGE"),
  proposalId,
  proposalFormId,
  fieldId: proposalFieldId,
  response: z.string(),
  // beforeFieldResponsesId is the field responses ID before this action is applied.
  // It' undefined iff there were no prior field responses.
  beforeFieldResponsesId: z.string().optional(),
  // afterFieldResponsesId is the field responses ID after this action is applied.
  afterFieldResponsesId: z.string(),
});

export const proposalFieldTitleChange = z.object({
  type: z.literal("PROPOSAL_FIELD_TITLE_CHANGE"),
  proposalFormId,
  fieldId: proposalFieldId,
  fieldTitle: z.string(),
});

export const proposalFieldDescriptionChange = z.object({
  type: z.literal("PROPOSAL_FIELD_DESCRIPTION_CHANGE"),
  proposalFormId,
  fieldId: proposalFieldId,
  fieldDescription: z.string().optional(),
});

// Note that if there's an existing proposal form we merge proposals in,
// overwriting the old version of any given proposal, but *not* removing
// proposals which were previously present but aren't in this action.
export const proposalFormExternalSync = z.object({
  type: z.literal("PROPOSAL_FORM_EXTERNAL_SYNC"),
  proposalFormId,
  proposals: z.record(z.string(), proposalWithFieldResponses),
  fields: z.array(fieldSchema),
});

export const funderBudgetChange = z.object({
  type: z.literal("FUNDER_BUDGET_CHANGE"),
  funderId,
  budget: z.number(),
});

const funderSettlementBudgetChange = z.object({
  type: z.literal("FUNDER_SETTLEMENT_BUDGET_CHANGE"),
  funderId,
  budget: z.number(),
});

const funderRatingsImport = z.object({
  type: z.literal("FUNDER_RATINGS_IMPORT"),
  funderId,
  scores: z.array(
    z.object({
      recommenderId,
      proposalId,
      rating: z.number().optional(),
    }),
  ),
});

const funderRatingChange = z.object({
  type: z.literal("FUNDER_RATING_CHANGE"),
  funderId,
  recommenderId,
  proposalId,
  rating: z.number().optional(),
});

// Ratings are whole stars 0–5. 0 is a real rating ("this evaluation
// contributes nothing" — the implicit default for no-note evaluations),
// distinct from an omitted rating, which clears the score. The rule is
// enforced here on NEW dispatches only (via `invalidNewActionReason`, called
// from the Server's PerformAction acceptance path) — the wire schemas above
// deliberately stay `z.number()`, because historical actions with
// out-of-band ratings could exist in the log and still flow to catching-up
// clients through `actionMessage`; tightening the schema itself would trip
// the client's schema-skew guard and halt that client's sync.
const isValidNewRating = (rating: number | undefined): boolean =>
  rating === undefined ||
  (Number.isInteger(rating) && rating >= 0 && rating <= 5);

/**
 * Semantic validation for newly dispatched actions, beyond what the wire
 * schema can enforce (see the comment above). Returns a human-readable
 * rejection reason, or null when the action is acceptable.
 */
export function invalidNewActionReason(
  payload: StampableAction,
): string | null {
  if (payload.type === "FUNDER_RATING_CHANGE") {
    if (!isValidNewRating(payload.rating)) {
      return "Rating must be a whole number from 0 to 5";
    }
  }

  if (payload.type === "FUNDER_RATINGS_IMPORT") {
    if (payload.scores.some((entry) => !isValidNewRating(entry.rating))) {
      return "Ratings must be whole numbers from 0 to 5";
    }
  }

  return null;
}

// LEGACY: wiped every funder's ratings in the round at once. No longer
// dispatched — the admin tool clears one funder at a time via
// CLEAR_FUNDER_RATINGS below — but historical event logs still replay it, so
// it must keep parsing and applying exactly as it always did.
const funderRatingsClear = z.object({
  type: z.literal("FUNDER_RATINGS_CLEAR"),
});

// Clears ONE funder's ratings (their whole `scores` map). Deliberately a
// distinct action type rather than an optional `funderId` on the legacy
// round-wide action above: object schemas strip unknown keys, so a client
// running a pre-deploy bundle would parse a widened payload successfully,
// silently drop the funderId, and apply the round-wide semantics to its local
// aggregate — wiping every funder's ratings on that client. The skew guard in
// `client/serverMessagePolicy.ts` only fires on payloads a build CANNOT parse,
// so a separate type is what makes an old client refuse this action (and
// prompt for a refresh) instead of misinterpreting it. `funderId` is required
// for the same reason: this action can never degrade into the round-wide form
// by losing a field.
const clearFunderRatings = z.object({
  type: z.literal("CLEAR_FUNDER_RATINGS"),
  funderId,
});

const funderProposalWeightChange = z.object({
  type: z.literal("FUNDER_PROPOSAL_WEIGHT_CHANGE"),
  funderId,
  proposalId,
  weight: z.number().nullable(),
});

const funderProposalExclusionToggle = z.object({
  type: z.literal("FUNDER_PROPOSAL_EXCLUSION_TOGGLE"),
  funderId,
  proposalId,
  excluded: z.boolean(),
});

const funderRecommenderWeightChange = z.object({
  type: z.literal("FUNDER_RECOMMENDER_WEIGHT_CHANGE"),
  funderId,
  recommenderId,
  weight: z.number().nullable(),
});

const setTrackSettings = z.object({
  type: z.literal("SET_TRACK_SETTINGS"),
  track: z.enum(["MAIN", "FAIRNESS", "FREEDOM"]),
  status: trackStatusSchema,
  evaluationsDueDate: z.string().optional(),
});

const investigationInterestChange = z.object({
  type: z.literal("INVESTIGATION_INTEREST_CHANGE"),
  proposalId,
  memberId: userId,
  interest: z.number().nullable().optional(),
  confirmedReviewer: z.boolean().optional(),
});

const reviewProgressChange = z.object({
  type: z.literal("REVIEW_PROGRESS_CHANGE"),
  proposalId,
  memberId: userId,
  progress: z.number().nullable(),
});

const recusalVoteOption = z.enum(["y", "n", "a"]);

export type RecusalVote = z.infer<typeof recusalVoteOption>;

const proposalDisclosureChange = z.object({
  type: z.literal("PROPOSAL_DISCLOSURE_CHANGE"),
  timestamp: actionTimestamp,
  proposalId,
  userId,
  hasDisclosure: z.boolean(),
  selfRecused: z.boolean(),
  disclosure: z.string(),
});

const recusalVote = z.object({
  type: z.literal("RECUSAL_VOTE"),
  timestamp: actionTimestamp,
  proposalId,
  votingUserId: userId,
  subjectUserId: userId,
  vote: recusalVoteOption,

  // Imported event from when voting was done in a spreadsheet.
  // Do not use this in the app.
  legacy: z.literal(true).optional(),
});

const recommenderOutsideDollarSubstitutionRateChange = z.object({
  type: z.literal("RECOMMENDER_OUTSIDE_DOLLAR_SUBSTITUTION_RATE_CHANGE"),
  evaluatorId,
  outsideDollarSubstitutionRate: z.number(),
});

const oneTimeAdminAction1 = z.object({
  type: z.literal("ONE_TIME_ADMIN_ACTION_1"),
});

const oneTimeAdminAction2 = z.object({
  type: z.literal("ONE_TIME_ADMIN_ACTION_2"),
});

const matchingAdjustmentIntensityChange = z.object({
  type: z.literal("MATCHING_ADJUSTMENT_INTENSITY_CHANGE"),
  matchingAdjustmentIntensity: z.number(),
});

const funderMatchingBudgetRatioChange = z.object({
  type: z.literal("FUNDER_MATCHING_BUDGET_RATIO_CHANGE"),
  funderId,
  matchingBudgetRatio: z.number().min(0).max(1),
});

const funderUsesMatchingChange = z.object({
  type: z.literal("FUNDER_USES_MATCHING_CHANGE"),
  funderId,
  usesMatching: z.boolean(),
});

const odsrMasterAdjustmentChange = z.object({
  type: z.literal("ODSR_MASTER_ADJUSTMENT_CHANGE"),
  odsrMasterAdjustment: z.number(),
});

const resetFunderIncrementalEvals = z.object({
  type: z.literal("RESET_FUNDER_INCREMENTAL_EVALS"),
  funderId,
});

export const roundMemberRole = z.enum(["admin", "funder", "recommender"]);

export type RoundMemberRole = z.infer<typeof roundMemberRole>;

export const createRoundWizardRound = z.object({
  description: roundDescriptionSchema.optional(),
  name: z.string().min(1, "Round name is required"),
  isSfcRound: z.boolean(),
  // Defaults to true (a normal track round) when omitted. When false, the
  // round is "trackless": ordinary recommenders sit in MAIN and track UI is hidden.
  usesTracks: z.boolean().optional(),
  // Omitted in historical events; new submissions default to false.
  allowsAnonymization: z.boolean().optional(),
  // Legacy: the wizard no longer collects this (the identifier is set when a
  // proposal form is attached), but events recorded before that carry one
  // and must keep it through replay.
  roundIdentifier: z.string().trim().optional(),
  budget: z.number().min(1, "Budget is required"),
  initials: z
    .string()
    .min(1, "Initials are required")
    .max(3, "Initials should be 1-3 characters")
    .transform((val) => val.toUpperCase()),
  // The hold proposal and default form ids the client minted for this round.
  // Recorded here so replaying this event reproduces them exactly; optional
  // only for events created before this field existed (the reducer derives a
  // stable id for those). New rounds always set them.
  holdProposalId: proposalId.optional(),
  defaultProposalFormId: proposalFormId.optional(),
});

export const customField = z.object({
  id: proposalFieldId,
  title: z.string().min(1, "Title is required"),
  description: z.string().optional(),
});

export const simpleProposal = z.object({
  id: proposalId,
  proposalFormId,
  shortname: z.string().min(1, "Title is required"),
  longname: z.string(),
  requestAmount: z.number().min(1, "Amount must not be 0"),
  fieldResponses: z.record(proposalFieldId, z.string()),
});

export const createRoundWizardProposal = simpleProposal
  .omit({ id: true, fieldResponses: true })
  .extend({
    proposalId,
    customFields: z.array(customField.extend({ value: z.string().optional() })),
  });

export const createRoundWizardMember = z.object({
  id: z.string(),
  roles: z.array(roundMemberRole).min(1, "Role is required"),
  initials: z
    .string()
    .min(1, "Initials are required")
    .max(3, "Initials should be 1-3 characters")
    .transform((val) => val.toUpperCase()),
  deanonymize: z.boolean().optional(),
});

const createRoundWizard = z.object({
  type: z.literal("CREATE_ROUND_WIZARD"),
  timestamp: actionTimestamp,
  round: createRoundWizardRound,
  proposal: createRoundWizardProposal.optional(),
  members: z.array(createRoundWizardMember),
});

// CREATE_OR_UPDATE_ROUND_MEMBER is used by the *regular round* modals:
// * <EditMemberModal /> and
// * <CreateMemberModal />
// Both on the /members page (<MembersPage />)
//
// Note also the related ADD_ROUND_MEMBER
export const createRoundMember = createRoundWizardMember.extend({
  type: z.literal("CREATE_OR_UPDATE_ROUND_MEMBER"),
  timestamp: actionTimestamp,
  budget: z.number().optional(),
  isRestrictedFunder: z.boolean().optional(),
  hiddenRecommenderMemberIds: z.array(z.string()).optional(),
});

export const proposalBaseRequestAmountChange = z.object({
  type: z.literal("PROPOSAL_BASE_REQUEST_AMOUNT_CHANGE"),
  proposalFormId,
  proposalId,
  baseRequestAmount: z.number(),
});

export const roundAdvancedAnalyticsChange = z.object({
  type: z.literal("ROUND_ADVANCED_ANALYTICS_CHANGE"),
  advancedAnalyticsEnabled: z.boolean(),
});

export const importProposals = z.object({
  type: z.literal("IMPORT_PROPOSALS"),
  // Application synthesizes ADD_PROPOSAL for new proposals, whose submitted
  // datetimes should be the import's creation time — not application-time
  // wall clock, which differs on every replay.
  timestamp: actionTimestamp,
  customFields: z.array(customField),
  proposals: z.array(simpleProposal),
});

const noop = z.object({
  type: z.literal("NOOP"),
});

// User actions are initiated by a human (directly via WebSocket, or via a tRPC
// route that acts on behalf of a user). They require actingAsUserId and are
// checked by isActionPermitted.
export const validNewAction = z.discriminatedUnion("type", [
  createRound,
  addRoundMember,
  addProposalForm,
  addOrUpdateProposalFormField,
  addEvaluator,
  addProposal,
  startEvaluation,
  removeRound,
  removeRoundMember,
  removeProposalForm,
  proposalFormRoundIdentifierChange,
  attachApplicationForm,
  applicationResponseInclusion,
  detachApplicationForm,
  applicationFormFieldMappingChange,
  applicationFormOptionValueChange,
  removeProposalFormField,
  removeEvaluator,
  removeProposal,
  readdProposal,
  roundNameChange,
  roundDescriptionChange,
  roundArchivedChange,
  roundCompletedChange,
  roundApplicationsOpenChange,
  roundApplicationAccessChange,
  adminChangeRoundMemberProperty,
  roundMemberPropertyChange,
  authorizeAgentTokenForRound,
  deauthorizeAgentTokenForRound,
  evaluatorPropertyChange,
  funderBudgetChange,
  funderSettlementBudgetChange,
  funderRatingsImport,
  funderRatingChange,
  funderRatingsClear,
  clearFunderRatings,
  funderProposalWeightChange,
  funderProposalExclusionToggle,
  funderRecommenderWeightChange,
  incrementalEvalsPush,
  incrementalEvalsUndo,
  incrementalEvalsRedo,
  evaluationFeedbackChange,
  evaluationNotesChange,
  generalNotesChange,
  evaluationStateChange,
  evaluationSetHidden,
  evaluationMvfChange,
  matchingMvfChange,
  evaluationSetZero,
  evaluationFinalityChange,
  evaluationNegativeValueChange,
  evaluationsNormalize,
  proposalFormFieldChange,
  proposalLongnameChange,
  proposalShortnameChange,
  proposalFieldResponseChange,
  proposalFieldTitleChange,
  proposalFieldDescriptionChange,
  proposalFormExternalSync,
  setTrackSettings,
  investigationInterestChange,
  reviewProgressChange,
  proposalDisclosureChange,
  recusalVote,
  oneTimeAdminAction1,
  oneTimeAdminAction2,
  funderMatchingBudgetRatioChange,
  funderUsesMatchingChange,
  resetFunderIncrementalEvals,
  createRoundWizard,
  createRoundMember,
  proposalBaseRequestAmountChange,
  roundAdvancedAnalyticsChange,
  importProposals,
  noop,
]);

export type ValidNewAction = z.infer<typeof validNewAction>;

// These actions are no longer used but not in upcastAction because we still
// process them (don't upcast them to new actions).
const deprecatedAction = z.discriminatedUnion("type", [
  notesAndFeedbackVisibilityChange,
  matchingAdjustmentIntensityChange,
]);

// Actions which we no longer create and upcast to currently used actions.
export const removedAction = z.discriminatedUnion("type", [
  evaluationStatusChange,
  evaluationExpectedMatchingFractionChange,
  recommenderOutsideDollarSubstitutionRateChange,
  odsrMasterAdjustmentChange,
]);

export type RemovedAction = z.infer<typeof removedAction>;

// Action lifecycle:
// 1) New actions must be added to validNewAction.
// 2) Deprecated actions remain processable as-is so historical rows replay,
//    but cannot be newly submitted.
// 3) Removed actions remain parseable and are upcast to current actions during
//    reducer replay in core.ts.
// 4) WebSocket creation of deprecated and removed actions is blocked via
//    NON_CREATABLE_ACTION_TYPES.
//
// Keep upcastAction and round/upcast.ts in sync.
//
// Combined union of all current action types (used by the reducer, storage, etc.)
export const currentAction = z.union([validNewAction, deprecatedAction]);

export type CurrentAction = z.infer<typeof currentAction>;

// Actions in the DB could include both current and removed actions (which we'll
// upcast to a current action)
export const dbAction = z.union([currentAction, removedAction]);
export type DbAction = z.infer<typeof dbAction>;

export type AddRound = z.infer<typeof createRound>;
export type AddEvaluator = z.infer<typeof addEvaluator>;
export type MVFChange = z.infer<typeof evaluationMvfChange>;
export type MatchingMVFChange = z.infer<typeof matchingMvfChange>;
export type StartEvaluation = z.infer<typeof startEvaluation>;
export type EvaluationStatusChange = z.infer<typeof evaluationStatusChange>;
export type EvaluationStateChange = z.infer<typeof evaluationStateChange>;
export type EvaluationSetHidden = z.infer<typeof evaluationSetHidden>;
export type IncrementalEvalsPush = z.infer<typeof incrementalEvalsPush>;
export type ProposalFormExternalSync = z.infer<typeof proposalFormExternalSync>;
export type AddProposalForm = z.infer<typeof addProposalForm>;
export type AttachApplicationForm = z.infer<typeof attachApplicationForm>;
export type DetachApplicationForm = z.infer<typeof detachApplicationForm>;
export type ApplicationFormFieldMappingChange = z.infer<
  typeof applicationFormFieldMappingChange
>;
export type ApplicationFormOptionValueChange = z.infer<
  typeof applicationFormOptionValueChange
>;
export type EvaluationNotesChange = z.infer<typeof evaluationNotesChange>;
export type GeneralNotesChange = z.infer<typeof generalNotesChange>;
export type FieldCreateSchema = z.infer<typeof fieldCreateSchema>;
export type AddOrUpdateProposalFormField = z.infer<
  typeof addOrUpdateProposalFormField
>;
export type ProposalFieldResponseChange = z.infer<
  typeof proposalFieldResponseChange
>;
export type EvaluationFeedbackChange = z.infer<typeof evaluationFeedbackChange>;
export type NotesAndFeedbackVisibility = z.infer<
  typeof notesAndFeedbackVisibility
>;
export type NoteChecklist = {
  explainWhy: boolean;
  beSpecific: boolean;
  shareWhatYouKnow: boolean;
  engageDirectly: boolean;
  coverMultipleAngles: boolean;
  giveClearRecommendation: boolean;
  shareWorldview: boolean;
};
export type NotesAndFeedbackVisibilityChange = z.infer<
  typeof notesAndFeedbackVisibilityChange
>;
export type InvestigationInterestChange = z.infer<
  typeof investigationInterestChange
>;
export type RoundNameChange = z.infer<typeof roundNameChange>;
export type RoundArchivedChange = z.infer<typeof roundArchivedChange>;
export type AddRoundMember = z.infer<typeof addRoundMember>;
export type RoundMemberPropertyChange = z.infer<
  typeof roundMemberPropertyChange
>;
export type CreateRoundMember = z.infer<typeof createRoundMember>;
export type CreateRoundWizardMember = z.infer<typeof createRoundWizardMember>;
export type CustomField = z.infer<typeof customField>;
export type SimpleProposal = z.infer<typeof simpleProposal>;
export type CreateRoundWizardProposal = z.infer<
  typeof createRoundWizardProposal
>;
export type CreateRoundWizardRound = z.infer<typeof createRoundWizardRound>;

export type UserActionType = ValidNewAction["type"];
export type DeprecatedAction = z.infer<typeof deprecatedAction>;
export type DeprecatedActionType = DeprecatedAction["type"];
export type RemovedActionType = RemovedAction["type"];
// Includes both current and removed types because DB payloads can be historical.
export type ActionType = DbAction["type"];

export const DEPRECATED_ACTION_TYPES: DeprecatedActionType[] =
  deprecatedAction.options.map((option) => option.shape.type.value);

export const REMOVED_ACTION_TYPES: RemovedActionType[] =
  removedAction.options.map((option) => option.shape.type.value);

// Actions which remain valid in stored history but cannot be newly submitted.
export const NON_CREATABLE_ACTION_TYPES: ActionType[] = [
  ...DEPRECATED_ACTION_TYPES,
  ...REMOVED_ACTION_TYPES,
];

// The payload types that carry actionTimestamp — exactly the handlers that
// persist a time into the aggregate. Keep in sync with the schemas above;
// the reducer's fallback injection and the client queue migration
// (SPAStorageAdapter.init) both consume it.
export const TIMESTAMPED_ACTION_TYPES: ReadonlySet<string> = new Set([
  "CREATE_ROUND",
  "ROUND_ARCHIVED_CHANGE",
  "ROUND_COMPLETED_CHANGE",
  "CREATE_ROUND_WIZARD",
  "ADD_ROUND_MEMBER",
  "CREATE_OR_UPDATE_ROUND_MEMBER",
  "START_EVALUATION",
  "EVALUATION_FEEDBACK_CHANGE",
  "EVALUATION_NOTES_CHANGE",
  "EVALUATION_MVF_CHANGE",
  "MATCHING_MVF_CHANGE",
  "EVALUATION_SET_ZERO",
  "EVALUATION_SET_HIDDEN",
  "EVALUATION_FINALITY_CHANGE",
  "EVALUATION_NEGATIVE_VALUE_CHANGE",
  "EVALUATION_STATE_CHANGE",
  "PROPOSAL_DISCLOSURE_CHANGE",
  "RECUSAL_VOTE",
  "IMPORT_PROPOSALS",
]);

// A CurrentAction as call sites build it: the creation timestamp may be
// omitted because stampActionTimestamp fills it in at the dispatch
// chokepoints. (Distributes over the union, relaxing `timestamp` on the
// TIMESTAMPED_ACTION_TYPES members only.)
type WithOptionalTimestamp<T> = T extends { timestamp: string }
  ? Omit<T, "timestamp"> & { timestamp?: string }
  : T;
export type StampableAction = WithOptionalTimestamp<CurrentAction>;

// Only for NEW submissions, never historical replay. Keep the choice in the
// event so subsequent rebuilds preserve it.
export function applyNewRoundDefaults(
  action: StampableAction,
): StampableAction {
  if (action.type === "CREATE_ROUND" && action.allowsAnonymization == null) {
    return { ...action, allowsAnonymization: false };
  }

  if (
    action.type === "CREATE_ROUND_WIZARD" &&
    action.round.allowsAnonymization == null
  ) {
    return {
      ...action,
      round: { ...action.round, allowsAnonymization: false },
    };
  }

  return action;
}

// Stamp a freshly created action with its creation time and new-round defaults.
// Called at the
// message-creation chokepoints (Client dispatch, undo/redo, the round-creation
// route) so every copy of the payload — optimistic apply, server persist,
// confirm re-apply, future replays — carries the SAME value. No-op for types
// that don't persist a time, and for payloads already stamped (idempotent, so
// a resubmitted action keeps its original creation time). `fresh` overrides
// an existing stamp — for REDO, which reuses the original action's payload
// but is a new touch happening now.
export function stampActionTimestamp(
  action: StampableAction,
  opts: { fresh?: boolean } = {},
): CurrentAction {
  action = applyNewRoundDefaults(action);

  if (!TIMESTAMPED_ACTION_TYPES.has(action.type)) {
    return action as CurrentAction;
  }

  const existing = (action as { timestamp?: string }).timestamp;

  if (existing != null && !opts.fresh) {
    return action as CurrentAction;
  }

  return { ...action, timestamp: new Date().toISOString() } as CurrentAction;
}
